/**
 * The register cart's money, in integer cents, rounded the way the server charges it
 * (backend/payments/pricing.py: cents() and tax_on() are Decimal ROUND_HALF_UP).
 *
 * The cart shows the server's own answer (POST /api/checkout/price, the same
 * pricing.price_items the order is created with); this is only what it shows until
 * that answer arrives, and it uses the SAME rounding, so the two never differ
 * ($34.00 x 8.75% = 2.975 -> $2.98, never the float $2.97).
 */

/** The lines POST /api/checkout/orders and /price take, from the cart. */
export function checkoutLines(cart) {
  return (cart || []).map((i) => ({
    menu_item_id: i.menuItemId,
    quantity: i.quantity,
    modifier_option_ids: (i.modifiers || []).map((m) => m.id).filter(Boolean),
    notes: i.notes || null,
  }));
}

/** A decimal number as [integer digits, scale]: 8.75 -> [875, 2]. Exact for what an API sends. */
function decimalParts(value) {
  const text = String(value ?? 0).trim();
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!m) {
    // exponent form (1e-7) - not something a price or a rate is; fall back to 12 places
    return decimalParts(Number(text).toFixed(12));
  }
  const frac = (m[3] || '').replace(/0+$/, '');
  const digits = Number(`${m[2] || '0'}${frac}`) * (m[1] ? -1 : 1);
  return [digits, frac.length];
}

/** half-up of n / d for integers, d > 0 (Decimal ROUND_HALF_UP: away from zero on .5). */
function divHalfUp(n, d) {
  const q = Math.floor((2 * Math.abs(n) + d) / (2 * d));
  return n < 0 ? -q : q;
}

/** Dollars -> cents, half-up (pricing.cents). */
export function toCents(dollars) {
  const [digits, scale] = decimalParts(dollars);
  return scale <= 2 ? digits * 10 ** (2 - scale) : divHalfUp(digits, 10 ** (scale - 2));
}

/** Tax on a subtotal in cents at a percent rate, half-up (pricing.tax_on). */
export function taxCents(subtotalCents, ratePercent) {
  const [rate, scale] = decimalParts(ratePercent);
  return divHalfUp(subtotalCents * rate, 100 * 10 ** scale);
}

/** {subtotal_cents, tax_cents, total_cents} of a cart; tax null when the rate is not set. */
export function cartMoney(cart, ratePercent) {
  const subtotal = (cart || []).reduce((sum, item) => {
    const unit = toCents(item.price) + (item.modifiers || []).reduce((s, m) => s + toCents(m.price || 0), 0);
    return sum + unit * (item.quantity || 0);
  }, 0);
  const tax = ratePercent == null ? null : taxCents(subtotal, ratePercent);
  return { subtotal_cents: subtotal, tax_cents: tax, total_cents: subtotal + (tax || 0) };
}
